'use client'

import styled from '@emotion/styled'
import InsightsIcon from '@mui/icons-material/Insights'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import TargetIcon from '@mui/icons-material/TrackChanges'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { motion } from 'framer-motion'

const Section = styled.section`
  padding: 96px 0 128px;
  background: linear-gradient(180deg, #ffffff 0%, #fafafa 50%, #ffffff 100%);
`

const IconBox = styled(Box)`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(39, 177, 198, 0.2),
    rgba(39, 177, 198, 0.1)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  transition: transform 0.3s;
`

const StyledCard = styled(Card)`
  height: 100%;
  background: #fafafa;
  background-image: none;
  border: none;
  border-radius: 12px;
  box-shadow:
    0 1px 2px -1px rgba(0, 0, 0, 0.1),
    0 4px 6px -1px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
  position: relative;

  &:hover {
    box-shadow:
      0 1px 2px -1px rgba(0, 0, 0, 0.1),
      0 8px 10px -1px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);

    .icon-box {
      transform: scale(1.1);
    }
  }
`

const pillars = [
  {
    icon: <InsightsIcon sx={{ fontSize: 32, color: '#27b1c6' }} />,
    title: 'Robust Data Foundation',
    description:
      'We build on comprehensive data sources including on-chain analytics, market microstructure, and sentiment analysis to ensure our decisions are well-informed.',
  },
  {
    icon: <TargetIcon sx={{ fontSize: 32, color: '#27b1c6' }} />,
    title: 'Battle-Tested Models',
    description:
      'Our neural networks undergo rigorous statistical validation and performance testing across multiple market conditions before deployment.',
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 32, color: '#27b1c6' }} />,
    title: 'Thorough Backtesting',
    description:
      'Every strategy is extensively validated against historical data across different market regimes to verify consistent performance before going live.',
  },
  {
    icon: <ShieldOutlinedIcon sx={{ fontSize: 32, color: '#27b1c6' }} />,
    title: 'Risk-First Mentality',
    description:
      'We prioritize capital preservation through conservative position sizing, confidence-based allocation, and continuous risk monitoring.',
  },
]

export default function WhyTrustSection() {
  return (
    <Section>
      <Container maxWidth='lg'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Typography
            variant='h3'
            sx={{ textAlign: 'center', fontWeight: 700, mb: 2, color: '#171717' }}
          >
            Why Trust Polar Tensor
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '1.125rem',
              maxWidth: 700,
              mx: 'auto',
              mb: 8,
            }}
          >
            Built on rigorous methodology and proven principles that prioritize
            reliability over speculation
          </Typography>
        </motion.div>

        <Grid container spacing={3}>
          {pillars.map((pillar, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                style={{ height: '100%' }}
              >
                <StyledCard elevation={0}>
                  <CardContent sx={{ p: 4 }}>
                    <IconBox className='icon-box'>{pillar.icon}</IconBox>
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 700, mb: 2, color: '#171717' }}
                    >
                      {pillar.title}
                    </Typography>
                    <Typography sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                      {pillar.description}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Section>
  )
}
